import { styled } from '@mui/material';
import IconButton from '@mui/material/IconButton';

export const Page = styled('main')(({ theme }) => ({
  minHeight: 'calc(100vh - 70px)',
  padding: 24,
  boxSizing: 'border-box',
  backgroundColor: theme.palette.novotalk.$backgroundColorMedium,
  '@media (max-width: 700px)': { padding: 12 },
}));

export const Panel = styled('section')(({ theme }) => ({
  maxWidth: 1440,
  margin: '0 auto',
  padding: 24,
  backgroundColor: theme.palette.novotalk.$backgroundColor,
  borderRadius: 8,
  boxShadow: '0 4px 18px rgba(15, 23, 42, 0.08)',
  '@media (max-width: 700px)': { padding: 14 },
}));

export const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 18,
});

export const Title = styled('h1')(({ theme }) => ({
  margin: 0,
  fontSize: 24,
  fontWeight: 700,
  color: theme.palette.novotalk.$textColorDarkBlue,
}));

export const PrimaryButton = styled('button')(({ theme }) => ({
  minHeight: 40,
  padding: '0 16px',
  border: 0,
  borderRadius: 6,
  cursor: 'pointer',
  fontWeight: 700,
  color: theme.palette.novotalk.$textColorReversed,
  backgroundColor: theme.palette.novotalk.$ctaButtonColor,
  '&:hover': { backgroundColor: theme.palette.novotalk.$ctaButtonColorHover },
  '&:disabled': { opacity: 0.5, cursor: 'default' },
}));

export const SecondaryButton = styled(PrimaryButton)(({ theme }) => ({
  color: theme.palette.novotalk.$textColorDisabled,
  backgroundColor: theme.palette.novotalk.$backgroundColorMedium,
  '&:hover': { backgroundColor: theme.palette.novotalk.$backgroundColorMediumHover },
}));

export const Toolbar = styled('div')({
  display: 'flex',
  gap: 12,
  marginBottom: 14,
  '@media (max-width: 700px)': { flexDirection: 'column' },
});

export const SearchInput = styled('input')(({ theme }) => ({
  width: 320,
  maxWidth: '100%',
  minHeight: 40,
  padding: '0 12px',
  boxSizing: 'border-box',
  border: `1px solid ${theme.palette.novotalk.$inputBorderColor}`,
  borderRadius: 5,
  fontSize: 14,
  outline: 'none',
  '&:focus': { borderColor: theme.palette.novotalk.$activeColor },
}));

export const TableWrap = styled('div')(({ theme }) => ({
  minHeight: 590,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
  borderRadius: 6,
  '@media (max-width: 1100px)': {
    overflowX: 'auto',
    '& > div': { minWidth: 1130 },
  },
}));

export const Table = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
});

export const Th = styled('th')(({ theme }) => ({
  padding: '11px 10px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 700,
  color: theme.palette.novotalk.$textColorDisabledLight,
  backgroundColor: theme.palette.novotalk.$backgroundColorMedium,
  borderBottom: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
}));

export const Td = styled('td')(({ theme }) => ({
  padding: '10px',
  fontSize: 13,
  color: theme.palette.novotalk.$textColorDarkBlue,
  borderBottom: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
}));

export const Status = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone',
})(({ tone }) => {
  const tones = {
    neutral: { backgroundColor: '#eef1f5', color: '#5b6b82' },
    info: { backgroundColor: '#e8f4fb', color: '#287fa8' },
    success: { backgroundColor: '#e4f6ee', color: '#2f8f69' },
    error: { backgroundColor: '#fdeaee', color: '#c74867' },
  };
  return {
    ...(tones[tone] || tones.neutral),
    maxWidth: '100%',
    padding: '4px 10px',
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    overflow: 'hidden',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.3,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
});

export const CommentMarker = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#2f9d70',
  fontSize: 18,
  fontWeight: 700,
  cursor: 'default',
}));

export const DateCell = styled('span')(({ theme }) => ({
  display: 'block',
  color: theme.palette.novotalk.$textColorDarkBlue,
  fontSize: 13.5,
  fontWeight: 600,
  '& small': {
    display: 'block',
    marginTop: 2,
    color: theme.palette.novotalk.$textColorDisabledLight,
    fontSize: 12.5,
    fontWeight: 400,
  },
}));

export const IssueMarker = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  color: '#c98216',
  fontSize: 18,
  fontWeight: 700,
  cursor: 'default',
  '& > span': { fontSize: 11 },
});

export const Actions = styled('div')({ display: 'flex', gap: 4 });

export const ActionButton = styled(IconButton)({
  width: 34,
  height: 34,
  borderRadius: 5,
  '&:disabled': { opacity: 0.35 },
});

export const FormGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 14,
  paddingTop: 8,
  '@media (max-width: 650px)': { gridTemplateColumns: '1fr' },
});

export const Field = styled('label')(({ theme }) => ({
  display: 'grid',
  gap: 6,
  fontSize: 12,
  fontWeight: 700,
  color: theme.palette.novotalk.$textColorDarkBlue,
  '&[data-wide="true"]': { gridColumn: '1 / -1' },
  '& > input:not([type="checkbox"]), & > select, & > textarea': {
    width: '100%',
    minWidth: 0,
    height: 34,
    minHeight: 34,
    padding: '0 8px',
    boxSizing: 'border-box',
    border: `1px solid ${theme.palette.novotalk.$inputBorderColor}`,
    borderRadius: 4,
    backgroundColor: theme.palette.novotalk.$backgroundColor,
    color: theme.palette.novotalk.$textColorDarkBlue,
    fontSize: 14,
  },
  '& > input:not([type="checkbox"]):focus, & > select:focus, & > textarea:focus': {
    borderColor: theme.palette.novotalk.$inputBorderColorActive,
    outline: 'none',
  },
  '& .MuiInputBase-root': {
    height: 34,
    minHeight: 34,
    borderRadius: 4,
    color: theme.palette.novotalk.$textColorDarkBlue,
    backgroundColor: theme.palette.novotalk.$backgroundColor,
    fontFamily: 'inherit',
    fontSize: 14,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
    borderColor: theme.palette.novotalk.$inputBorderColor,
    borderRadius: 4,
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.novotalk.$inputBorderColor,
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
    borderColor: theme.palette.novotalk.$inputBorderColorActive,
  },
  '& .MuiInputAdornment-root .MuiIconButton-root': {
    padding: 4,
    '& svg': { fontSize: 18 },
  },
  '& .MuiSelect-select': {
    minHeight: '0 !important',
    height: 34,
    padding: '0 32px 0 8px !important',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
  },
  '& > textarea': {
    height: 'auto',
    minHeight: 80,
    padding: 8,
    resize: 'vertical',
  },
}));

export const CheckboxRow = styled('label')(({ theme }) => ({
  minHeight: 34,
  display: 'inline-flex',
  alignItems: 'center',
  justifySelf: 'start',
  gap: 8,
  fontSize: 14,
  fontWeight: 400,
  cursor: 'pointer',
  '& .MuiCheckbox-root': { padding: 0, color: '#cfd8e3' },
  '& .Mui-checked': { color: `${theme.palette.novotalk.$activeColor} !important` },
}));

export const Empty = styled('div')(({ theme }) => ({
  padding: 36,
  textAlign: 'center',
  color: theme.palette.novotalk.$textColorDisabledLight,
}));